import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ChartModule } from 'primeng/chart';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AppService } from 'app/app.service';
import { ChartService } from '../../chart.service';

interface TimeByVariant {
  typeDescription: string[];
  time: number[];
}

@Component({
  selector: 'app-pie-chart-variant',
  standalone: true,
  imports: [
    CommonModule,
    ChartModule,
    BreadcrumbComponent
  ],
  templateUrl: './pie-chart-variant.component.html',
  styleUrl: './pie-chart-variant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PieChartVariantComponent implements OnInit, OnDestroy {

  documentStyle = getComputedStyle(document.documentElement);

  chartData: any;
  chartTitle: string = '';
  timeByVariant?: TimeByVariant;
  options: any;
  lightModeOptions: any;
  darkModeOptions: any;
  previousTheme: string | undefined;
  aircraftIndex?: number;

  theme$?: string;
  themeSub?: Subscription;
  aircraftIndexSub?: Subscription;

  constructor(
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    public chartService: ChartService
  ) { }

  ngOnInit(): void {
    this.themeSub = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
      this.cdr.markForCheck();
    });

    this.aircraftIndexSub = this.route.params.subscribe(params => {
      this.aircraftIndex = params['index'];
    })

    this.getAircraftData();
    this.loadOptions();

  }

  ngDoCheck(): void {
    const currentTheme = this.theme$;
    if (this.previousTheme !== currentTheme) {
      this.previousTheme = currentTheme;
      this.options = this.toggleThemeOptions();

      if (this.chartData) {
        this.chartTitle = 'Time by Variant(s)';
        this.formatTimeByVariantChartData();
      }
      else {
        this.loadEmptyChart();
      }
    }
  }
  toggleThemeOptions(): any {
    if (this.theme$ === 'light') {
      return this.lightModeOptions;
    } else {
      return this.darkModeOptions;
    }
  }

  getAircraftData() {
    const keys = Object.keys(localStorage);
    const chartKeys = keys.filter(key => key.endsWith('chartAircraft'));
    chartKeys.forEach(key => {
      const chartData = localStorage.getItem(key);
      if (chartData) {
        const parsedData = JSON.parse(chartData);
        if (this.aircraftIndex) {
          const timeByVariant = parsedData.data[this.aircraftIndex].timeByType;
          const variantName = [];
          const variantTime = [];
          if (timeByVariant) {
            for (let i = 0; i < timeByVariant.length; i++) {
              variantName.push(timeByVariant[i].typeDescription);
              variantTime.push(
                this.chartService.convertMinutesToHours(timeByVariant[i].time)
              );
              
            }
            let variantData: TimeByVariant = 
              {
                typeDescription: variantName,
                time: variantTime
              };
            
            this.timeByVariant = variantData;
            this.formatTimeByVariantChartData();
          }
        }
      }
    });
  }

  loadOptions(): void {
    this.lightModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#35454c'
          }
        }
      }
    };

    this.darkModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#e7e7e7'
          }
        }
      }
    };

    this.options = this.lightModeOptions;
  }

  formatTimeByVariantChartData(): void {
    if (this.theme$ === 'light') {
      this.chartData = {
        labels: this.timeByVariant?.typeDescription,
        datasets: [
          {
            data: this.timeByVariant?.time,
            backgroundColor: this.chartService.lightColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };
    }
    else {
      this.chartData = {
        labels: this.timeByVariant?.typeDescription,
        datasets: [
          {
            data: this.timeByVariant?.time,
            backgroundColor: this.chartService.darkColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };
    }
  }

  loadEmptyChart(): void {
    if (this.theme$ === 'light') {
      this.chartData = {
        labels: ['N/A'],
        datasets: [
          {
            data: [100],
            backgroundColor: '#1c80cf50',
            hoverBackgroundColor: '#75BEF850'
          }
        ]
      };
    }
    else {
      this.chartData = {
        labels: ['N/A'],
        datasets: [
          {
            data: [100],
            backgroundColor: '#1c80cf50',
            hoverBackgroundColor: '#75BEF850'
          }
        ]
      };
    }
  }
  

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.aircraftIndexSub?.unsubscribe();
  }

}
